import type { CSSProperties } from "react";
import type { SingleLineTextInputProps } from "@narmi/design_system";
import { TextInput } from "@narmi/design_system";
import ContextForm from "../ContextForm";

// Default error messages
const DEFAULT_EMAIL_ERROR = "Please enter a valid email address.";
const DEFAULT_MAX_LENGTH_ERROR = "Exceeds character limits";

// Standalone email validation function for external use
export function validateEmail(
  value?: string | null,
  disabled?: boolean,
  errorMsg?: string,
  maxLength?: number,
  maxLengthErrorMsg?: string
): string | null | undefined {
  if (disabled) return null;
  if (!value) return errorMsg;

  // Check maxLength first if provided
  if (maxLength && value.length > maxLength) {
    return maxLengthErrorMsg;
  }

  // at least one character that's not a space or @ before the @
  // at least one character that's not a space or @ after the @, followed by a dot
  // at least one character that's not a space or @ after the dot
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Ensure email has no whitespace
  if (/\s/.test(value)) {
    return errorMsg;
  }

  if (!emailRegex.test(value)) {
    return errorMsg;
  }

  return null;
}

type EmailFormFieldProps = {
  required?: boolean;
  disabled?: boolean;
  label?: string;
  fieldStyle?: CSSProperties;
  className?: string;
  errorLabel?: string;
  maxLength?: number;
  onBlur?: SingleLineTextInputProps["onBlur"];
};

const EmailFormField = ({
  label,
  required = false,
  disabled,
  fieldStyle,
  className,
  errorLabel = DEFAULT_EMAIL_ERROR,
  maxLength,
  onBlur,
}: EmailFormFieldProps) => {
  return (
    <ContextForm.Field
      required={required}
      validate={(value: string | null | undefined) =>
        validateEmail(value, disabled, errorLabel, maxLength, DEFAULT_MAX_LENGTH_ERROR)
      }
      style={fieldStyle}
      className={className}
    >
      <TextInput
        name="email"
        field="email"
        label={label || "Email"}
        type="email"
        data-test-element="email-input"
        disabled={disabled}
        maxLength={maxLength}
        onBlur={onBlur}
      />
    </ContextForm.Field>
  );
};

export default EmailFormField;
